import { formatNumero } from "./formatoNumero";

/**
 * Embudo de frecuencia: un tramo por etapa de gasto (ej. "Público nuevo" →
 * "Calentamiento" → "Cierre"). Lo que lo vuelve diagnóstico y no decorativo:
 * **el ancho de cada banda es `porcion_gasto`**, nunca un ancho fijo
 * decreciente. Un embudo sano se angosta parejo hacia abajo; uno con una
 * etapa desatendida sale con forma de diamante — el problema se lee de un
 * vistazo, sin mirar un número.
 *
 * Cada banda se dibuja como un trapecio con `clip-path`: arriba con su
 * propio ancho, abajo con el de la banda siguiente, para que el conjunto
 * quede como una silueta continua (sin cortes entre bandas) en vez de una
 * pila de rectángulos sueltos.
 */

export interface AnuncioBanda {
  nombre: string;
  lectura: string;
  desplazamiento: string | null;
}

export interface BandaEmbudo {
  nombre: string;
  porcion_gasto: number;
  slots_ocupados: number;
  slots_objetivo: number;
  anuncios: AnuncioBanda[];
}

interface EmbudoFrecuenciaProps {
  bandas: BandaEmbudo[];
}

/** Porcentaje de sangrado a cada lado para un ancho relativo dado. */
const sangria = (porcion: number) => ((1 - porcion) / 2) * 100;

/** Trapecio: arriba con el ancho de esta banda, abajo con el de la siguiente. */
function clipDeBanda(porcion: number, porcionSiguiente: number): string {
  const a = sangria(porcion);
  const b = sangria(porcionSiguiente);
  return `polygon(${a}% 0%, ${100 - a}% 0%, ${100 - b}% 100%, ${b}% 100%)`;
}

/**
 * Normaliza contra la banda más gorda (`porcion / maxPorcion`) para que esa
 * banda ocupe el ancho completo y el resto se lea en proporción a ella.
 *
 * Blindado contra los dos casos que rompen la cuenta: lista vacía (no hay
 * `max` que sacar — el componente ni llega a llamar a esto, corta antes) y
 * todas las bandas en $0 de gasto (`maxPorcion` da 0 y `porcion / 0` sería
 * `NaN` metido en un `clip-path`, que rompe el render). En vez de eso, si no
 * hay ninguna porción positiva se devuelve 0 para cada banda: la fórmula del
 * trapecio con porción 0 da un ancho de banda invisible pero válido, nunca
 * `NaN`.
 */
function normalizarPorciones(bandas: BandaEmbudo[]): number[] {
  const positivas = bandas.map((banda) => Math.max(banda.porcion_gasto, 0));
  const maxPorcion = positivas.length > 0 ? Math.max(...positivas) : 0;
  if (!(maxPorcion > 0)) return positivas.map(() => 0);
  return positivas.map((porcion) => porcion / maxPorcion);
}

// Tonos de marca que se ciclan si hay más de tres bandas; con un color por
// banda se distingue dónde termina una y empieza la otra dentro de la
// silueta continua del trapecio apilado.
const COLORES_BANDA = ["#1644EB", "#01269B", "#0C36B8", "#3A5CE0"];

export const EmbudoFrecuencia = ({ bandas }: EmbudoFrecuenciaProps) => {
  if (bandas.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#02062B]/[0.18] p-6 text-center text-[13px] text-[#02062B]/[0.55]">
        No hay bandas de frecuencia para este período.
      </div>
    );
  }

  const porciones = normalizarPorciones(bandas);

  return (
    <div className="flex flex-col">
      {bandas.map((banda, indice) => {
        const porcion = porciones[indice];
        const esUltima = indice === bandas.length - 1;
        const porcionSiguiente = esUltima ? porcion : porciones[indice + 1];
        const faltaSlot = banda.slots_ocupados < banda.slots_objetivo;

        return (
          <div key={banda.nombre} className="flex items-stretch gap-4">
            {/* La silueta: puramente el ancho proporcional al gasto. Sin
                texto adentro a propósito — una banda angosta (o en $0) no
                tiene lugar para nada legible, y el número ya se lee al
                costado. */}
            <div className="relative w-44 shrink-0 sm:w-56">
              <div
                className="absolute inset-0"
                style={{
                  clipPath: clipDeBanda(porcion, porcionSiguiente),
                  backgroundColor: COLORES_BANDA[indice % COLORES_BANDA.length],
                  borderBottom: esUltima ? undefined : "2px solid white",
                }}
              />
            </div>

            <div className="flex-1 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className="text-[13px] font-semibold text-brand-black">{banda.nombre}</span>
                <span className="text-[12px] text-[#02062B]/[0.55]">
                  {formatNumero(banda.porcion_gasto)} del gasto
                </span>
              </div>
              <p className="mt-0.5 text-[12px] text-[#02062B]/[0.55]">
                {banda.slots_ocupados} de {banda.slots_objetivo} slots
                {faltaSlot && (
                  <span className="ml-1 font-medium text-[#C33B36]">· te falta un slot</span>
                )}
              </p>

              {banda.anuncios.length > 0 && (
                <div className="mt-2.5 flex flex-col gap-1.5">
                  {banda.anuncios.map((anuncio) => (
                    <div key={anuncio.nombre} className="rounded-lg bg-[#02062B]/[0.03] py-1.5 px-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12px] font-medium text-brand-black">{anuncio.nombre}</span>
                        <span className="text-[11px] text-[#02062B]/[0.5]">{anuncio.lectura}</span>
                      </div>
                      {anuncio.desplazamiento && (
                        <p className="mt-0.5 text-[10px] font-medium text-[#C33B36]">
                          {anuncio.desplazamiento}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmbudoFrecuencia;
